import { Component, Element, Host, Prop, State, h } from '@stencil/core';

import { onStateFieldAnswered } from '../../../actions/actions.answers';
import { onFieldValidated } from '../../../actions/actions.validation';
import { onValidateConsent, onValidateZipCode } from '../../../actions/actions.validation.customer';
import { shiftDigitalTradeInFormInitiation } from '../../../tracking/tracking.shift-digital';

@Component({
  tag: 'customer-info',
  styleUrl: 'customer-info.css',
})
export class PersonalInfo {
  @Element() el: HTMLElement;

  @Prop() showFormValidation: boolean;
  @Prop() fieldErrors: Set<string>;
  @Prop() firstName: string;
  @Prop() lastName: string;
  @Prop() email: string;
  @Prop() phone: string;
  @Prop() zip: string;
  @Prop() legalAcceptance: boolean;
  @Prop() showOnlyConsent: boolean;
  @Prop() showLegalAcceptance: boolean = true;

  @State() hasPhoneValidationErrorState: boolean = false;
  @State() hasZipValidationErrorState: boolean = false;
  @State() isPhoneDirtyState: boolean = false;
  @State() isZipDirtyState: boolean = false;
  @State() breakpoints: { [key: string]: boolean } = {};

  // TODO: replace with responsiveContainer method we use in condition quiz
  observer: ResizeObserver;

  connectedCallback() {
    this.observer = new ResizeObserver((entries) => this.onResize(entries));
    this.observer.observe(this.el);
  }

  disconnectedCallback() {
    this.observer.disconnect();
  }

  render() {
    const isPhoneDirty = !this.showOnlyConsent && (this.showFormValidation || this.isPhoneDirtyState);
    const isZipDirty = !this.showOnlyConsent && (this.showFormValidation || this.isZipDirtyState);
    const hasPhoneValidationError = !this.showOnlyConsent && (this.hasPhoneValidationErrorState || this.fieldErrors.has('phone'));
    const hasZipValidationError = !this.showOnlyConsent && (this.hasZipValidationErrorState || this.fieldErrors.has('zip'));
    let phoneZipErrors;

    if (isPhoneDirty && hasPhoneValidationError && isZipDirty && hasZipValidationError) {
      phoneZipErrors = 'Please enter a valid phone number and ZIP code.';
    } else if (isPhoneDirty && hasPhoneValidationError) {
      phoneZipErrors = 'Please enter a valid phone number.';
    } else if (isZipDirty && hasZipValidationError) {
      phoneZipErrors = 'Please enter a valid ZIP code.';
    }

    if (!this.showLegalAcceptance) {
      onStateFieldAnswered('contactInfo', 'legalAcceptance', true);
    }

    return (
      <Host data-testId="customerInfo">
        {!this.showOnlyConsent && (
          <div class="@container">
            <div class="flex flex-row items-start gap-2 pb-2">
              <question-text
                data-testId="contactFirstName"
                id="firstName"
                canonicalName="contactInfo"
                fieldName="firstName"
                class={{ 'basis-full': !this.breakpoints.md, 'basis-0 flex-[1]': this.breakpoints.md }}
                type="name"
                label="First Name"
                value={this.firstName}
                isDirty={this.showFormValidation}
                errorMessage="Please enter a valid first name."
                hasError={this.fieldErrors.has('firstName')}
                required
                onFocus={() => shiftDigitalTradeInFormInitiation()}
              />
              <question-text
                data-testId="contactLastName"
                id="lastName"
                canonicalName="contactInfo"
                fieldName="lastName"
                class={{ 'basis-full': !this.breakpoints.md, 'basis-0 flex-[1]': this.breakpoints.md }}
                type="name"
                label="Last Name"
                value={this.lastName}
                isDirty={this.showFormValidation}
                errorMessage="Please enter a valid last name."
                hasError={this.fieldErrors.has('lastName')}
                required
                onFocus={() => shiftDigitalTradeInFormInitiation()}
              />
            </div>
            <div class="flex flex-row flex-wrap items-start gap-2 pb-2">
              <div class="basis-full @2lg:flex-[1]">
                <question-text
                  data-testId="contactEmail"
                  id="email"
                  canonicalName="contactInfo"
                  fieldName="email"
                  class="flex-[1]"
                  type="email"
                  label="Email Address"
                  value={this.email}
                  isDirty={this.showFormValidation}
                  hasError={this.fieldErrors.has('email')}
                  required
                  onFocus={() => shiftDigitalTradeInFormInitiation()}
                />
              </div>

              <div>
                <div class="flex flex-row gap-2">
                  <question-text
                    data-testId="contactPhone"
                    id="phone"
                    canonicalName="contactInfo"
                    fieldName="phone"
                    class="flex-[1]"
                    type="phone"
                    label="Phone Number"
                    value={this.phone}
                    isDirty={this.showFormValidation}
                    onValidation={(evt) => {
                      this.hasPhoneValidationErrorState = !evt.detail.isValid;
                      this.isPhoneDirtyState = evt.detail.isDirty;
                    }}
                    hasError={hasPhoneValidationError}
                    errorMessage=""
                    required
                    onFocus={() => shiftDigitalTradeInFormInitiation()}
                  />
                  <question-text
                    data-testId="contactZip"
                    id="zip"
                    canonicalName="contactInfo"
                    fieldName="zip"
                    class="basis-24"
                    type="zip"
                    label="ZIP Code"
                    value={this.zip}
                    isDirty={this.showFormValidation}
                    onValidation={async (evt) => {
                      if (evt.detail.isValid && evt.detail.isDirty) {
                        await onValidateZipCode(this.zip);
                      }
                      this.hasZipValidationErrorState = !evt.detail.isValid;
                      this.isZipDirtyState = evt.detail.isDirty;
                    }}
                    hasError={hasZipValidationError}
                    errorMessage=""
                    required
                    onFocus={() => shiftDigitalTradeInFormInitiation()}
                  />
                </div>
                {phoneZipErrors && (
                  <div>
                    <span class="phone-zip-error-text">{phoneZipErrors}</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
        {this.showLegalAcceptance && (
          <div class="pb-2">
            <question-boolean
              id="legalAcceptance"
              canonicalName="contactInfo"
              fieldName="legalAcceptance"
              errorMessage="Please review and accept the Program Terms and Conditions."
              hasError={this.fieldErrors.has('legalAcceptance')}
              isDirty={this.showFormValidation}
              answer={{ value: this.legalAcceptance }}
              whenChanged={(value) => {
                onStateFieldAnswered('contactInfo', 'legalAcceptance', value);
                onFieldValidated('contactInfo', 'legalAcceptance', value);
                if (this.showOnlyConsent) {
                  onValidateConsent(value);
                }
              }}
              inputStyle="checkbox"
              heading={
                <div class="whitespace-normal">
                  I accept the{' '}
                  <ico-hyperlink noUnderline href={process.env.TOS_URL} target="_blank">
                    Program Terms and Conditions
                  </ico-hyperlink>
                  .
                </div>
              }
            />
          </div>
        )}
      </Host>
    );
  }

  onResize(entries: ResizeObserverEntry[]) {
    const entry = entries[0];
    if (entry) {
      const contentBoxSize = Array.isArray(entry.contentBoxSize) ? entry.contentBoxSize[0] : entry.contentBoxSize;
      const size = contentBoxSize.inlineSize;

      // TODO: We should find a way to generalize this so it can be repeated in other components
      this.breakpoints = {
        sm: size >= 640,
        md: size >= 768,
        lg: size >= 1024,
        xl: size >= 1280,
        '2xl': size >= 1536,
      };
    }
  }
}
